import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { ConfirmationModalProps } from "./ConfirmationModal.types";

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  eyebrow,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  variant = "danger",
  requireReason = false,
  reason,
  reasonLabel = "Reason (required)",
  reasonPlaceholder = "Please provide a reason...",
  onReasonChange,
  children,
}) => {
  const handleConfirm = () => {
    if (requireReason && !reason?.trim()) return;
    onConfirm?.();
  };
  const isDanger = variant === "danger";
  const Icon = isDanger ? AlertTriangle : CheckCircle2;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm z-50 animate-fade-in" />
        <Dialog.Content
          data-testid="confirmation-modal"
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "w-[calc(100vw-2rem)] max-w-lg overflow-hidden rounded-xl",
            "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800",
            "shadow-2xl animate-dialog-fade-in",
          )}
        >
          <div className="flex items-start gap-4 border-b border-slate-200 dark:border-slate-800 px-6 py-5">
            <div
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                isDanger
                  ? "bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300"
                  : "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300",
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              {eyebrow && (
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {eyebrow}
                </p>
              )}
              <Dialog.Title
                data-testid="modal-title"
                className="text-lg font-semibold text-slate-950 dark:text-slate-50"
              >
                {title}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                data-testid="modal-close-btn"
                className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="px-6 py-5">
            {description && (
              <Dialog.Description
                data-testid="modal-description"
                className="text-sm leading-6 text-slate-600 dark:text-slate-300"
              >
                {description}
              </Dialog.Description>
            )}

            {children}

            {requireReason && (
              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {reasonLabel}
                </label>
                <textarea
                  data-testid="modal-reason-textarea"
                  value={reason || ""}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onReasonChange?.(e.target.value)
                  }
                  placeholder={reasonPlaceholder}
                  className={cn(
                    "w-full h-24 px-3 py-2 text-sm rounded-md",
                    "border border-slate-200 dark:border-slate-700",
                    "bg-white dark:bg-slate-800",
                    "text-slate-900 dark:text-slate-100",
                    "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                    "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                    "resize-none",
                  )}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-950/40">
            <Button
              data-testid="modal-cancel-btn"
              variant="outline"
              onClick={onCancel ?? (() => onOpenChange?.(false))}
            >
              {cancelLabel}
            </Button>
            <Button
              data-testid="modal-confirm-btn"
              variant={isDanger ? "danger" : "primary"}
              onClick={handleConfirm}
              disabled={requireReason && !reason?.trim()}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
